import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { FormatterInteger } from '@/src/utils/FormatterKm';

const technicalMetas = [
  { meta: 'brand' },
  { meta: 'model' },
  { meta: 'year' },
  { meta: 'model_year' },
  { meta: 'km', unit: 'km', format: true },
  { meta: 'kg', unit: 'kg', format: true },
  { meta: 'hk', format: true },
  { meta: 'width', unit: 'mm', format: true },
  { meta: 'lenght', unit: 'mm', format: true },
  { meta: 'length', unit: 'mm', format: true },
  { meta: 'weight', unit: 'kg', format: true },
  { meta: 'payload', unit: 'kg', format: true },
  { meta: 'gearbox-automatic' },
  { meta: 'van_segment' },
  { meta: 'front_tyres' },
  { meta: 'rear_tyres' },
  { meta: 'km-t' },
  { meta: 'hours' },
  { meta: 'technical-approved', bool: true },
  { meta: 'eu-approved-until' },
  { meta: 'chassisno' },
  { meta: 'chassisno2' },
  { meta: 'regno' },
  { meta: 'euro' },
  { meta: 'girkasse', translatable: true },
  { meta: 'number-seats' },
  { meta: 'machine-total-width', unit: 'mm', format: true },
  { meta: 'mast-height-extended', unit: 'mm', format: true },
  { meta: 'max-working-height', unit: 'mm', format: true },
  { meta: 'load-capacity-max-height', unit: 'mm', format: true },
  { meta: 'last-approved-inspection' },
  { meta: 'driving-ban-since' },
  { meta: 'number-of-shafts' },
];

function getMeta(meta_key: string) {
  let metaReturn = {} as any;
  technicalMetas.forEach((meta) => {
    if (meta.meta === meta_key) {
      metaReturn = meta;
    }
  });
  return metaReturn;
}

export default function ItemSpecifications({ ad }: { ad: any }) {
  const t = useTranslations('ItemSpecification');

  if (!ad || !ad.ad_meta || !ad.ad_meta.length) {
    return null;
  }

  const orderedMeta = [] as any;
  technicalMetas.forEach((meta) => {
    ad.ad_meta.forEach((adMeta: { meta_key: string; meta_value: any }) => {
      if (meta.meta === adMeta.meta_key) {
        orderedMeta.push(adMeta);
      }
    });
  });
  ad.ad_meta.forEach((adMeta: { meta_key: string; meta_value: any }) => {
    let ignore = false;
    if (adMeta.meta_value === null) ignore = true;
    const ignoreKeys = [
      'description',
      'title',
      'api_',
      'finn_',
      'youtube',
      'Blocket',
      'blocket',
    ];
    ignoreKeys.forEach((key) => {
      if (adMeta.meta_key.includes(key)) ignore = true;
    });
    if (
      [
        'price',
        'body_type',
        'engine_power',
        'engine_volume',
        'exterior_colour_main',
        'fuel_type',
        'millage',
        'model',
        'number_of_seats',
        'registration',
        'registration_class',
        'sales_form',
        'transmission',
        'transmission_specification',
        'wheel_drive',
        'wheel_drive_specification',
      ].includes(adMeta.meta_key)
    )
      ignore = true;
    if (ignore) {
      return;
    }
    let found = false;
    orderedMeta.forEach((ordered: { meta_key: string }) => {
      if (adMeta.meta_key === ordered.meta_key) {
        found = true;
      }
    });
    if (!found) {
      orderedMeta.push(adMeta);
    }
  });

  const numRows = Math.ceil(orderedMeta.length / 2);

  if (orderedMeta.length === 0) {
    return null;
  }

  return (
    <>
      <h3 className='text-2xl text-[#2D8477]'>
        {t('technicalSpecifications')}
      </h3>
      <div
        className={`grid-flow-col gap-x-6 md:grid lg:block xl:grid`}
        style={{ gridTemplateRows: 'repeat(' + numRows + ', minmax(0, 1fr))' }}
      >
        {orderedMeta.map(
          (meta: { meta_key: string; meta_value: any }, index: number) => {
            let value = meta.meta_value;
            const metaData = getMeta(meta.meta_key);
            if (metaData && metaData.bool) {
              value = meta.meta_value ? t('yes') : t('no');
            }
            if (metaData && metaData.translatable) {
              value = t(value);
            }
            if ([0, 1, '0', '1'].includes(meta.meta_value)) {
              value = meta.meta_value ? t('yes') : t('no');
            }
            if (
              (metaData && metaData.format) ||
              typeof meta.meta_value === 'number'
            ) {
              value = FormatterInteger(value);
            }
            let className = 'bg-[#133D37]';
            if (index % 2 === 1) {
              className = 'bg-[#0E302B]';
            }
            if (numRows % 2 === 1 && index >= numRows) {
              className =
                'bg-[#133D37] lg:bg-[#133D37] md:bg-[#0E302B] xl:bg-[#0E302B]';
              if (index % 2) {
                className =
                  'bg-[#0E302B] lg:bg-[#0E302B] md:bg-[#133D37] xl:bg-[#133D37]';
              }
            }
            return (
              <div
                key={meta.meta_key}
                className={`${className} flex justify-between px-4 py-2`}
              >
                <div>
                  <span className='font-bold capitalize'>
                    {t(meta.meta_key)
                      .replaceAll('ItemSpecification.', '')
                      .replaceAll('-', ' ')
                      .replaceAll('_', ' ')}
                  </span>
                  {metaData && metaData.unit && <span> ({metaData.unit})</span>}
                </div>
                <div>{value}</div>
              </div>
            );
          }
        )}
        {
          orderedMeta.length === 1 && (
            <div></div>
          ) /* Add empty div to limit the width of the single column */
        }
      </div>
    </>
  );
}
